/**
 * TOTEM EIDOS — accessori per lo schermo touch del negozio (attivi solo con la classe "eidos-totem" sul body,
 * cioe' con ?totem=… nell'indirizzo oppure con uno schermo verticale da totem, vedi totem.ts):
 *
 *  1) CAMERA "sicura": niente trascinamento laterale (era il modo in cui il gioiello "spariva" fuori dallo
 *     schermo), zoom limitato fra due distanze ragionevoli, e un pulsante grande CENTRA che riporta il
 *     gioiello al centro (zoomTo del visore).
 *  2) TASTIERA sullo schermo: al posto di quella di Android. Quando si tocca un campo di testo (indirizzo,
 *     dedica) compare in basso una tastiera nostra; i campi hanno inputmode="none" cosi' quella di sistema
 *     non si apre. I tasti scrivono nel campo con un evento "input" vero (funziona sia con i campi React
 *     sia con la casella di Mapbox). Mentre la tastiera e' aperta il body ha la classe "eidos-tastiera-aperta"
 *     e la variabile CSS --eidos-tastiera-h (altezza della tastiera): totem.css accorcia la pagina di
 *     quell'altezza, cosi' il gioiello sale e non resta coperto.
 *
 * La VISTA INIZIALE PIU' VICINA (zoomToFactor) vale invece su TUTTI gli schermi (richiesta di Davide, 26 set).
 */
import {useShapeDiverStoreParameters} from "@AppBuilderLib/entities/parameter/model/useShapeDiverStoreParameters";
import {IShapeDiverParameter} from "@AppBuilderLib/entities/parameter/config/parameter";
import {useShapeDiverStoreViewport} from "@AppBuilderLib/entities/viewport/model/useShapeDiverStoreViewport";
import {useEffect, useLayoutEffect, useRef, useState} from "react";
import {asseVerticale, avviaAlzaPC, avviaZoomTotem, centraGioiello, fermaAlzaPC, fermaZoomTotem, zoomDedicaOff, zoomDedicaTesto} from "./zoomDedica";
import {ascoltaSchermoTelefono} from "./tabsTelefono";
import {totemVero} from "./totem";

const OTTANIO = "#134D5D";
const CREMA = "#F6F4F2";
/** quanto stretta e' l'inquadratura iniziale (il modello ShapeDiver ha "zoom factor 2", pensato largo) */
const ZOOM_SCHERMI = 1.3; // (5 ott) era 1,5: la coppia di orecchini pendenti usciva tagliata ai lati sul computer
const ZOOM_TOTEM = 1.15; // (27 set) piu' vicino: vedi anche inquadraTotem in zoomDedica.ts
/** (5 ott sera, Davide: "sul PC lo zoom mi sembra poco per collana e bracciale; la catena non e' importante che
 *  si veda tutta") sul COMPUTER il fattore dipende dal tipo di gioiello. Nel visore un fattore piu' BASSO
 *  avvicina (misurato: 2 -> distanza 126, 1,3 -> 82, 0,8 -> 51). Catena del bracciale tagliata ai lati: voluto. */
const ZOOM_PER_MODELLO: Record<string, number> = {
	Collana: 1.05, // con 0,95 la piastrina toccava il riquadro prezzo (la catena resta un po' tagliata in alto)
	Bracciale: 0.65, // piastrina due volte piu' grande, catena tagliata ai lati
};
function fattoreSchermi(nomeModello: string): number {
	return ZOOM_PER_MODELLO[nomeModello] ?? ZOOM_SCHERMI;
}
/** con la tastiera aperta ci si avvicina per leggere bene la dedica mentre la si scrive */
const ZOOM_TASTIERA = 0.7;

/** l'ultima camera vista da EidosCamera: la tastiera la usa per avvicinare/allontanare */
let cambiaZoom: ((fattore: number) => void) | null = null;

function eTotem(): boolean {
	return typeof document !== "undefined" && document.body.classList.contains("eidos-totem");
}

/** telefono: schermo piu' stretto di 600 px (vedi anche la regola @media in totem.css) */
function schermoStretto(): boolean {
	try {
		return window.matchMedia("(max-width: 599px)").matches;
	} catch {
		return false;
	}
}

/* ---------------------------------------------------------------- camera + pulsante CENTRA */
function EidosCamera({totem}: {totem: boolean}) {
	const viewports = useShapeDiverStoreViewport((s) => s.viewports);
	const lista = Object.values(viewports);
	// (9 ott 2026) telefono o computer si ridecide quando cambia la larghezza: nel negozio il configuratore parte in
	// una finestra nascosta (larga 0 = "telefono") e si apre dopo, larga come il computer
	const [stretto, setStretto] = useState(schermoStretto);
	useEffect(() => ascoltaSchermoTelefono(setStretto), []);

	// fattore di zoom corrente: totem fisso; computer per tipo di gioiello (il "Modello" scelto dal cliente)
	const fattoreRef = useRef(totem ? ZOOM_TOTEM : ZOOM_SCHERMI);
	const nomeModelloRef = useRef("");
	const parameterStores = useShapeDiverStoreParameters((s) => s.parameterStores);
	const chiaveNs = Object.keys(parameterStores).join("|");
	useEffect(() => {
		const {getParameter} = useShapeDiverStoreParameters.getState();
		const disiscrizioni: (() => void)[] = [];
		for (const ns of Object.keys(parameterStores)) {
			const st = getParameter(ns, "Modello");
			if (!st) continue;
			const leggi = () => {
				const p = st.getState() as IShapeDiverParameter<string>;
				const scelte = (p.definition as {choices?: string[]}).choices ?? [];
				const v = String(p.state.uiValue ?? "");
				const i = parseInt(v, 10);
				return isNaN(i) ? v : (scelte[i] ?? v);
			};
			const aggiorna = () => {
				const nome = leggi();
				nomeModelloRef.current = nome;
				// il fattore va cambiato PRIMA che arrivi il risultato del calcolo: il visore ri-inquadra con quello
				if (!totem) fattoreRef.current = fattoreSchermi(nome);
			};
			aggiorna();
			disiscrizioni.push(st.subscribe(aggiorna));
		}
		return () => disiscrizioni.forEach((d) => d());
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chiaveNs, totem]);

	useEffect(() => {
		for (const vp of lista) {
			try {
				const cam = vp.camera as unknown as {
					enablePan?: boolean;
					zoomRestriction?: {minDistance: number; maxDistance: number};
					zoomToFactor?: number;
					zoomTo?: () => Promise<boolean>;
				};
				if (!cam) continue;
				if (totem) {
					cam.enablePan = false;
					cam.zoomRestriction = {minDistance: 25, maxDistance: 400};
					// rotazione libera con il dito, anche attorno all'asse verticale (Davide, 28 set)
					const c2 = cam as unknown as {
						enableRotation?: boolean;
						rotationRestriction?: {minPolarAngle: number; maxPolarAngle: number; minAzimuthAngle: number; maxAzimuthAngle: number};
					};
					c2.enableRotation = true;
					// ⚠️ NON impostare rotationRestriction con Infinity: il visore lo legge come null e BLOCCA la rotazione
					// (28 set 2026, "l'oggetto risulta bloccato"). I limiti predefiniti sono gia' liberi.
					// (28 set notte) sul totem l'inquadratura dopo ogni calcolo la facciamo NOI (inquadraTotem):
					// il "Camera adjust" del visore partiva DOPO la nostra e la annullava (bracciale intero e
					// minuscolo, orecchini piccoli, collana con la piastrina sopra il prezzo)
					(cam as unknown as {autoAdjust?: boolean}).autoAdjust = false;
					// al primo disegno "totem" e' false e puo' essere partita la logica del computer: si spegne
					fermaAlzaPC();
				}
				// TELEFONO (3 ott 2026): anche sugli schermi stretti l'inquadratura la facciamo noi (piastrina +
				// un po' di catena, sopra il riquadro prezzo), come sul totem; il "Camera adjust" del visore
				// inquadrava tutta la catena e la piastrina finiva sotto il riquadro prezzo
				// (5 ott) sul computer resta lo zoomTo del visore (fattore ZOOM_SCHERMI): con l'inquadratura Eidos
				// la collana riempiva tutto il visore largo. Per la coppia di pendenti il fattore e' sceso a 1,3.
				if (stretto) {
					fermaAlzaPC(); // (9 ott) la finestra si e' ristretta: smette l'inquadratura del computer
					(cam as unknown as {autoAdjust?: boolean}).autoAdjust = false;
					avviaZoomTotem();
				} else if (!totem) {
					// COMPUTER (6 ott 2026): il visore NON inquadra piu' da solo dopo i calcoli (faceva un movimento, e
					// la nostra correzione un secondo: "rallenta vistosamente"); inquadriamo noi in UNA animazione, con la
					// sua stessa formula (calculateZoomTo) ma con la piastrina al centro e sopra il riquadro prezzo
					fermaZoomTotem(); // (9 ott) partito nella finestra nascosta del negozio (larga 0 = "telefono")
					const c4 = cam as unknown as {autoAdjust?: boolean; initialAutoAdjust?: boolean};
					c4.autoAdjust = false;
					c4.initialAutoAdjust = false;
					avviaAlzaPC();
				}
				(window as unknown as {__eidosCamera: unknown}).__eidosCamera = cam;
				cam.zoomToFactor = fattoreRef.current;
				// gioiello in piedi (modello dal 28 set): la camera parte DAVANTI (da -Y), appena dall'alto;
				// zoomTo tiene la direzione e regola la distanza
				if (asseVerticale() === 2) {
					try {
						const c3 = cam as unknown as {position: Float32Array; target: Float32Array};
						const t = c3.target;
						c3.position = new Float32Array([t[0], t[1] - 100, t[2] + 12]);
					} catch {
						/* ignora */
					}
				}
				// computer: niente zoomTo del visore (sarebbe un movimento in piu'): la prima inquadratura la fa
				// avviaAlzaPC appena la scena c'e'
				if (totem || stretto) cam.zoomTo?.();
				if (totem) avviaZoomTotem();
			} catch {
				/* camera non ancora pronta */
			}
		}
	}, [lista.length, totem, stretto]);

	// Il visore, a ogni aggiornamento del modello (es. giro sul retro), riapplica il suo "zoom factor" (2):
	// il nostro va quindi RIBADITO in continuazione, altrimenti dopo il primo aggiornamento si perde.
	// ⚠️ (28 set sera) "totem" e' false al primo disegno e diventa true subito dopo: senza questo aggiornamento
	// il ciclo di 1 secondo qui sotto continuava a ribadire il fattore del computer (1,5) anche sul totem
	useEffect(() => {
		fattoreRef.current = totem ? ZOOM_TOTEM : fattoreSchermi(nomeModelloRef.current);
	}, [totem]);
	useEffect(() => {
		const applica = (zoom: boolean) => {
			for (const vp of lista) {
				try {
					const cam = vp.camera as unknown as {zoomToFactor?: number; zoomTo?: () => Promise<boolean>};
					cam.zoomToFactor = fattoreRef.current;
					if (zoom) cam.zoomTo?.();
				} catch {
					/* ignora */
				}
			}
		};
		cambiaZoom = (fattore: number) => {
			fattoreRef.current = fattore;
			applica(true);
		};
		const timer = window.setInterval(() => applica(false), 1000);
		return () => {
			cambiaZoom = null;
			window.clearInterval(timer);
		};
	}, [lista.length]);

	// (16 ott 2026, Davide: «CENTRA con CENTRA GIOIELLO e farlo rifunzionare») con la dedica attiva il pulsante non
	// faceva nulla: ora centraGioiello inquadra sempre (piastrina con la dedica, altrimenti come all'avvio)
	const centra = () => centraGioiello();
	useEffect(() => () => fermaZoomTotem(), []);

	if (!totem || !lista.length) return null;
	return (
		<button
			type="button"
			className="eidos-centra"
			onPointerDown={(e) => {
				e.preventDefault(); // non togliere il fuoco al campo della dedica (la tastiera resta aperta)
				centra();
			}}
		>
			Centra gioiello
		</button>
	);
}

/* ---------------------------------------------------------------- tastiera sullo schermo */
const RIGHE_LETTERE = [
	["1", "2", "3", "4", "5", "6", "7", "8", "9", "0"],
	["q", "w", "e", "r", "t", "y", "u", "i", "o", "p"],
	["a", "s", "d", "f", "g", "h", "j", "k", "l", "'"],
	["z", "x", "c", "v", "b", "n", "m", ",", ".", "-"],
];
const RIGA_ACCENTI = ["à", "è", "é", "ì", "ò", "ù", "@", "/", "&"];
/** SIMBOLI per la dedica (Davide, 28 set 2026): un carattere ciascuno, disegnati dal motore (SIMBOLI_INCISIONE
 *  in mappa_gioiello.py). Compaiono solo quando si scrive la dedica (campo textarea), non per l'indirizzo. */
const RIGA_SIMBOLI = ["♥", "★", "✿", "☾", "ᴥ"];
const ETICHETTE_SIMBOLI: Record<string, string> = {"ᴥ": "🐻"};

function scrivi(el: HTMLInputElement | HTMLTextAreaElement, nuovo: string) {
	const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype;
	const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
	if (setter) setter.call(el, nuovo);
	else el.value = nuovo;
	el.dispatchEvent(new Event("input", {bubbles: true}));
	// la casella dell'indirizzo (Mapbox) cerca i suggerimenti sull'evento "keydown" di un tasto normale
	el.dispatchEvent(new KeyboardEvent("keydown", {bubbles: true, key: "a", keyCode: 65}));
	el.dispatchEvent(new KeyboardEvent("keyup", {bubbles: true, key: "a", keyCode: 65}));
}

function TotemTastiera() {
	const [campo, setCampo] = useState<HTMLInputElement | HTMLTextAreaElement | null>(null);
	const [maiuscole, setMaiuscole] = useState(false);
	const campoRef = useRef(campo);
	campoRef.current = campo;
	const boxRef = useRef<HTMLDivElement>(null);
	const ultimoCampoRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

	useEffect(() => {
		// i campi di testo non devono aprire la tastiera di Android
		const marca = () => {
			document
				.querySelectorAll<HTMLInputElement>('input[type="text"], input:not([type]), textarea')
				.forEach((i) => {
					if (i.getAttribute("inputmode") !== "none") i.setAttribute("inputmode", "none");
				});
		};
		marca();
		const oss = new MutationObserver(marca);
		oss.observe(document.body, {childList: true, subtree: true});

		// (3 ott 2026) la tastiera si apre SOLO dopo un tocco vero: un "focus" dato dal programma (es. la casella
		// dell'indirizzo che la libreria Mapbox rimette a fuoco quando si svuota, al ritorno del totem) NON la apre.
		// Era il caso della "tastiera che compare da sola senza nessuna scritta".
		let ultimoTocco = 0;
		const suFocus = (ev: FocusEvent) => {
			if (Date.now() - ultimoTocco > 1500) return;
			const t = ev.target as HTMLElement;
			if (t instanceof HTMLInputElement && (t.type === "text" || !t.getAttribute("type")))
				setCampo(t);
			else if (t instanceof HTMLTextAreaElement) setCampo(t);
		};
		const suClick = (ev: MouseEvent) => {
			const t = ev.target as HTMLElement;
			// clic fuori dal campo e fuori dalla tastiera: la tastiera si chiude
			if (!campoRef.current) return;
			if (t === campoRef.current || t.closest?.("[data-eidos-tastiera]")) return;
			// (16 ott 2026) CENTRA GIOIELLO non chiude la tastiera: chiudendola il pulsante scendeva sotto il dito e il
			// tocco andava perso («il pulsante non funziona»)
			if (t.closest?.(".eidos-centra")) return;
			if (t instanceof HTMLInputElement || t instanceof HTMLTextAreaElement) return;
			setCampo(null);
		};
		// tocco su un campo di testo: apre la tastiera anche se il browser non manda "focusin"
		const suTocco = (ev: PointerEvent) => {
			ultimoTocco = Date.now();
			const t = ev.target as HTMLElement;
			if (t instanceof HTMLInputElement && (t.type === "text" || !t.getAttribute("type")))
				setCampo(t);
			else if (t instanceof HTMLTextAreaElement) setCampo(t);
		};
		document.addEventListener("focusin", suFocus);
		document.addEventListener("pointerdown", suClick, true);
		document.addEventListener("pointerdown", suTocco);
		return () => {
			oss.disconnect();
			document.removeEventListener("focusin", suFocus);
			document.removeEventListener("pointerdown", suClick, true);
			document.removeEventListener("pointerdown", suTocco);
		};
	}, []);

	// tastiera aperta: la pagina si accorcia dell'altezza della tastiera (vedi totem.css) e il campo resta in vista
	useLayoutEffect(() => {
		const radice = document.documentElement;
		if (!campo) {
			document.body.classList.remove("eidos-tastiera-aperta");
			radice.style.setProperty("--eidos-tastiera-h", "0px");
			// tastiera chiusa: se la dedica e' vuota si torna alla vista normale; se c'e' testo resta lo zoom
			// sulla piastrina (lo toglie EidosVista quando il cliente cambia modello, forma, ecc.)
			const ultimo = ultimoCampoRef.current;
			if (!(ultimo instanceof HTMLTextAreaElement) || !ultimo.value.trim()) {
				zoomDedicaOff(ZOOM_TOTEM);
				cambiaZoom?.(ZOOM_TOTEM);
			}
			return;
		}
		ultimoCampoRef.current = campo;
		const r = boxRef.current?.getBoundingClientRect();
		const h = r ? window.innerHeight - r.top : 0; // dal bordo alto della tastiera al fondo dello schermo
		radice.style.setProperty("--eidos-tastiera-h", `${Math.round(h)}px`);
		document.body.classList.add("eidos-tastiera-aperta");
		// campo della dedica (textarea): zoom sulla piastrina; altri campi (indirizzo): gioiello un po' piu' vicino
		// (16 ott 2026) riaprendo la tastiera su una dedica gia' scritta il gioiello resta dove l'ha messo il cliente
		if (campo instanceof HTMLTextAreaElement) zoomDedicaTesto();
		else cambiaZoom?.(ZOOM_TASTIERA);
		const t = window.setTimeout(() => {
			try {
				campo.scrollIntoView({block: "center", behavior: "smooth"});
			} catch {
				/* ignora */
			}
		}, 250);
		return () => window.clearTimeout(t);
	}, [campo]);

	if (!campo) return null;

	const premi = (k: string) => {
		const el = campoRef.current;
		if (!el) return;
		const v = el.value ?? "";
		if (k === "⌫") scrivi(el, v.slice(0, -1));
		else if (k === "spazio") scrivi(el, v + " ");
		else if (k === "⇧") setMaiuscole((m) => !m);
		else if (k === "chiudi") {
			setCampo(null);
			el.blur();
			return;
		} else if (k === "invio") {
			// Invio: per la casella dell'indirizzo sceglie il primo suggerimento
			el.dispatchEvent(new KeyboardEvent("keydown", {bubbles: true, key: "Enter", keyCode: 13}));
			el.dispatchEvent(new KeyboardEvent("keyup", {bubbles: true, key: "Enter", keyCode: 13}));
			return;
		} else scrivi(el, v + (maiuscole ? k.toUpperCase() : k));
		el.focus();
	};

	const tasto = (k: string, largo?: number, evid?: boolean) => {
		const lettera = /^[a-zà-ü]$/i.test(k);
		const mostra =
			k === "⇧" ? (maiuscole ? "⇧ abc" : "⇧ ABC") : k === "spazio" ? " " : lettera && maiuscole ? k.toUpperCase() : (ETICHETTE_SIMBOLI[k] ?? k);
		return (
			<button
				key={k}
				type="button"
				className={"eidos-tasto" + (evid || (k === "⇧" && maiuscole) ? " eidos-tasto-evidenziato" : "")}
				style={{flex: largo ?? 1}}
				onPointerDown={(e) => {
					e.preventDefault(); // non togliere il focus al campo
					premi(k);
				}}
			>
				{mostra}
			</button>
		);
	};

	return (
		<div ref={boxRef} data-eidos-tastiera="1" className="eidos-tastiera">
			{campo instanceof HTMLTextAreaElement && (
				<div style={{display: "flex"}} className="eidos-tastiera-simboli">
					{RIGA_SIMBOLI.map((k) => tasto(k))}
				</div>
			)}
			<div style={{display: "flex"}}>{RIGA_ACCENTI.map((k) => tasto(k))}</div>
			{RIGHE_LETTERE.map((riga, i) => (
				<div key={i} style={{display: "flex"}}>
					{riga.map((k) => tasto(k))}
				</div>
			))}
			<div style={{display: "flex"}}>
				{tasto("⇧", 2)}
				{tasto("spazio", 6)}
				{tasto("⌫", 2)}
				{tasto("invio", 2, true)}
				{tasto("chiudi", 2)}
			</div>
		</div>
	);
}

export default function EidosTotem() {
	const [totem, setTotem] = useState(false);
	// (24 ott 2026) la tastiera Eidos (e i campi con inputmode="none") solo sul VERO totem: un iPad in verticale ha la
	// disposizione del totem ma usa la sua tastiera di sistema
	const [vero, setVero] = useState(false);
	useEffect(() => {
		setTotem(eTotem());
		setVero(totemVero());
	}, []);
	return (
		<>
			<EidosCamera totem={totem} />
			{totem && vero && <TotemTastiera />}
		</>
	);
}

export {OTTANIO, CREMA};
